import { useState } from 'react';
import usePageMeta from '../../hooks/usePageMeta';
import { Mail, Phone, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ContactPage() {
  usePageMeta({
    title: 'Contact Stratel',
    description: 'Get in touch with Stratel to discuss healthcare strategy, operational improvements, or compliance assistance.',
    keywords: 'contact Stratel, healthcare consulting contact, request consultation',
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    service: '',
    message: '',
    hp: '' // honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, hp: formData.hp || '' }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Failed to submit');
      }

      toast.success('Thanks — we received your message and will be in touch shortly.');
      setFormData({ name: '', email: '', organization: '', phone: '', service: '', message: '' });
    } catch (err) {
      console.error('Contact submit failed', err);
      toast.error(err.message || 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-display font-bold">
              Contact Us
            </h1>
            <p className="mt-6 text-xl text-secondary-300">
              Ready to transform your organization? Get in touch and let's discuss 
              how we can help you achieve your goals.
            </p>
          </div>
        </div>
      </section>

      {/* Contact form and info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-display font-bold text-secondary-900">
                Get in Touch
              </h2>
              <p className="mt-4 text-secondary-600">
                Have questions? We'd love to hear from you. Send us a message 
                and we'll respond as soon as possible.
              </p>

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-secondary-900">Email</p>
                    <p className="text-secondary-600">info@stratelplus.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-secondary-900">Phone</p>
                    <p className="text-secondary-600">(404) 913-5524</p>
                  </div>
                </div>
 
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-secondary-900">Address</p>
                    <p className="text-secondary-600">
                      8735 Dunwoody Place Ste R,
                      <br />
                      Atlanta, GA 30350
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="card p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="label">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="input"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="label">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="input"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="organization" className="label">
                      Organization
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      className="input"
                      placeholder="Company Name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="service" className="label">
                      Service of Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="">Select a service...</option>
                      <option value="consulting">Consulting Services</option>
                      <option value="training">Training Services (LMS)</option>
                      <option value="staffing">Staffing Services</option>
                      <option value="job_seekers">Job Seekers</option>
                      <option value="other">Other / Not Sure</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="message" className="label">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="input resize-none"
                      placeholder="Tell us about your project or questions..."
                    />
                  </div>
                </div>

                <input type="hidden" name="hp" value={formData.hp} onChange={(e) => setFormData({ ...formData, hp: e.target.value })} />
                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full sm:w-auto"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
